import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Tag, ChevronLeft, ChevronRight, Package, MessageCircle } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   ProductQuickViewClient — Modal rapide côté CLIENT (ShopDetail)

   Props :
     product   — document produit Firestore
     category  — { name } ou null
     shop      — document shop (pour WhatsApp)
     packInfo  — résultat de useShopPack (pour hasWhatsApp)
     onClose   — fermer le modal
───────────────────────────────────────────────────────────────── */
const ProductQuickViewClient = ({ product, category, shop, packInfo, onClose }) => {
  const [imgIdx, setImgIdx] = React.useState(0);
  const images = product?.images?.filter(Boolean) || [];
  const hasImages = images.length > 0;

  /* ── Escape ── */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  /* ── Bloquer scroll body ── */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const prevImg = useCallback(() =>
    setImgIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const nextImg = useCallback(() =>
    setImgIdx(i => (i + 1) % images.length), [images.length]);

  /* ── Swipe touch ── */
  const touchStart = React.useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? nextImg() : prevImg();
    touchStart.current = null;
  };

  /* ── WhatsApp ── */
  const handleWhatsApp = () => {
    const phone = shop?.whatsapp || shop?.phone;
    if (!phone) return;
    const clean = phone.replace(/\D/g, '');
    const prefix = clean.startsWith('0') ? '213' + clean.slice(1) : clean;
    const msg = encodeURIComponent(
      `Bonjour, je suis intéressé(e) par votre produit "${product.name}"${
        product.price ? ` (${product.price.toLocaleString()} DA)` : ''
      } sur Yeldi.`
    );
    window.open(`https://wa.me/${prefix}?text=${msg}`, '_blank');
  };

  const showWhatsApp = packInfo?.hasWhatsApp && (shop?.whatsapp || shop?.phone);

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'qvcFadeIn 0.18s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: 'var(--bg)',
          borderRadius: '22px 22px 0 0',
          maxHeight: '92dvh',
          overflowY: 'auto',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.28)',
          animation: 'qvcSlideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: 'var(--bg-elevated)',
          margin: '12px auto 0',
        }} />

        {/* ── CAROUSEL ── */}
        <div
          style={{
            width: '100%',
            height: hasImages ? 280 : 120,
            background: 'var(--bg-elevated)',
            position: 'relative',
            overflow: 'hidden',
            marginTop: 10,
          }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {hasImages ? (
            <>
              <img
                key={imgIdx}
                src={images[imgIdx]}
                alt={product.name}
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  animation: 'qvcFadeIn 0.2s ease',
                }}
              />
              {/* Gradient bas pour les dots */}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 70,
                background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
                pointerEvents: 'none',
              }} />

              {/* Flèches si plusieurs images */}
              {images.length > 1 && (
                <>
                  <button onClick={prevImg} style={navBtnStyle('left')}>
                    <ChevronLeft size={18} strokeWidth={2.5} />
                  </button>
                  <button onClick={nextImg} style={navBtnStyle('right')}>
                    <ChevronRight size={18} strokeWidth={2.5} />
                  </button>
                  {/* Dots */}
                  <div style={{
                    position: 'absolute', bottom: 10,
                    left: '50%', transform: 'translateX(-50%)',
                    display: 'flex', gap: 5,
                  }}>
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setImgIdx(i)}
                        style={{
                          width: i === imgIdx ? 18 : 6, height: 6,
                          borderRadius: 3, border: 'none',
                          background: i === imgIdx
                            ? 'var(--primary, #00c9a7)'
                            : 'rgba(255,255,255,0.45)',
                          cursor: 'pointer', padding: 0,
                          transition: 'all 0.25s',
                        }}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Compteur image */}
              {images.length > 1 && (
                <div style={{
                  position: 'absolute', top: 12, right: 50,
                  padding: '3px 9px', borderRadius: 99,
                  background: 'rgba(0,0,0,0.45)',
                  fontSize: 11, fontWeight: 700, color: '#fff',
                  backdropFilter: 'blur(6px)',
                }}>
                  {imgIdx + 1} / {images.length}
                </div>
              )}
            </>
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <Package size={32} strokeWidth={1.5} color="var(--text-3)" />
              <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 600 }}>
                Aucune photo
              </span>
            </div>
          )}

          {/* Bouton fermer */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 12, right: 12,
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(0,0,0,0.45)',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff',
            }}
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>

        {/* ── CORPS ── */}
        <div style={{ padding: '16px 20px 36px' }}>

          {/* Catégorie */}
          {category && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginBottom: 10,
              padding: '3px 10px', borderRadius: 99,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border, rgba(0,0,0,0.08))',
              fontSize: 11, fontWeight: 700,
              color: 'var(--text-3)',
            }}>
              <Tag size={10} strokeWidth={2.5} />
              {category.name}
            </div>
          )}

          {/* Nom + Prix */}
          <div style={{
            display: 'flex', alignItems: 'flex-start',
            justifyContent: 'space-between', gap: 12,
            marginBottom: 12,
          }}>
            <h2 style={{
              fontSize: '1.1rem', fontWeight: 800,
              color: 'var(--text)', lineHeight: 1.3,
              letterSpacing: '-0.3px', flex: 1, margin: 0,
            }}>
              {product.name}
            </h2>

            {product.price != null && product.showPrice !== false && (
              <div style={{
                flexShrink: 0,
                fontSize: '1.05rem', fontWeight: 800,
                color: 'var(--primary, #00c9a7)',
                background: 'rgba(0,201,167,0.08)',
                border: '1px solid rgba(0,201,167,0.22)',
                borderRadius: 10, padding: '5px 13px',
                whiteSpace: 'nowrap',
              }}>
                {product.price.toLocaleString()} DA
              </div>
            )}
          </div>

          {/* Description */}
          {product.description?.trim() ? (
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--text-2)',
              lineHeight: 1.7,
              margin: '0 0 20px',
            }}>
              {product.description}
            </p>
          ) : (
            <p style={{
              fontSize: '0.8rem',
              color: 'var(--text-3)',
              fontStyle: 'italic',
              margin: '0 0 20px',
            }}>
              Ce produit n'a pas encore de description.
            </p>
          )}

          {/* ── CTA WhatsApp (pack premium/pro uniquement) ── */}
          {showWhatsApp && (
            <>
              <div style={{
                height: 1,
                background: 'var(--border, rgba(0,0,0,0.07))',
                marginBottom: 16,
              }} />
              <button
                onClick={handleWhatsApp}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '0.85rem',
                  borderRadius: 14, border: 'none',
                  background: 'linear-gradient(135deg, #128C7E, #25D366)',
                  color: '#fff',
                  fontWeight: 700, fontSize: '0.95rem',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 4px 16px rgba(37,211,102,0.30)',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                <MessageCircle size={18} />
                Contacter via WhatsApp
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes qvcFadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes qvcSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

/* ── Style bouton navigation carousel ── */
const navBtnStyle = (side) => ({
  position: 'absolute',
  [side === 'left' ? 'left' : 'right']: 12,
  top: '50%', transform: 'translateY(-50%)',
  width: 34, height: 34, borderRadius: '50%',
  background: 'rgba(0,0,0,0.38)',
  border: '1px solid rgba(255,255,255,0.18)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#fff',
  zIndex: 10,
});

export default ProductQuickViewClient;